import { useEffect, useState, type ReactElement, type ReactNode } from "react";
import {
  Arc as KonvaArc,
  Ellipse as KonvaEllipse,
  Group as KonvaGroup,
  Layer,
  Line as KonvaLine,
  Path as KonvaPath,
  Rect as KonvaRect,
  RegularPolygon as KonvaRegularPolygon,
  Ring as KonvaRing,
  Stage,
  Star as KonvaStar,
  Text as KonvaText,
} from "react-konva";
import {
  toKonvaImageFrameProps,
  toKonvaRectNodeProps,
  toKonvaTextNodeProps,
  type SocialPostLayoutTemplateDefinitionV2,
  type SocialPostLayoutTemplateLayerV2,
} from "@runcheetah/shared";

import { resolveLayoutTemplatePasteboardFrame } from "@/pages/layout-templates-state";
import { cn } from "@/lib/utils";

type LayoutTemplateTextLayer = Extract<SocialPostLayoutTemplateLayerV2, { type: "text" }>;
type LayoutTemplateRectLayer = Extract<SocialPostLayoutTemplateLayerV2, { type: "rect" }>;
type LayoutTemplateImageLayer = Extract<
  SocialPostLayoutTemplateLayerV2,
  { type: "image" | "logo" }
>;
type LayoutTemplateShapeLayer = Extract<SocialPostLayoutTemplateLayerV2, { type: "shape" }>;
type LayoutTemplateFrameLayer = Extract<SocialPostLayoutTemplateLayerV2, { type: "frame" }>;

export type LayoutTemplateCanvasSurfaceRenderArgs = {
  pasteboardFrame: ReturnType<typeof resolveLayoutTemplatePasteboardFrame>;
  previewHeight: number;
  previewWidth: number;
  scale: number;
  sortedLayers: SocialPostLayoutTemplateLayerV2[];
};

const getSortedTemplateCanvasLayers = (
  template: SocialPostLayoutTemplateDefinitionV2,
): SocialPostLayoutTemplateLayerV2[] =>
  template.canonicalScene.layers.toSorted((left, right) => left.zIndex - right.zIndex);

const ImageFramePlaceholder = ({
  layer,
  scale,
}: {
  layer: LayoutTemplateImageLayer;
  scale: number;
}) => {
  const frameProps = toKonvaImageFrameProps(layer, scale, "local");
  const label = layer.type === "logo" ? "Logo" : "Generated image";

  return (
    <>
      <KonvaRect
        x={frameProps.x}
        y={frameProps.y}
        width={frameProps.width}
        height={frameProps.height}
        fill="rgba(15, 23, 42, 0.08)"
        stroke="rgba(15, 23, 42, 0.35)"
        dash={[8, 6]}
        cornerRadius={frameProps.cornerRadius}
      />
      <KonvaText
        x={frameProps.x + 12}
        y={frameProps.y + frameProps.height / 2 - 8}
        width={Math.max(0, frameProps.width - 24)}
        text={label}
        align="center"
        fill="rgba(15, 23, 42, 0.65)"
        fontSize={12}
        listening={false}
      />
    </>
  );
};

const ShapeLayerNode = ({ layer, scale }: { layer: LayoutTemplateShapeLayer; scale: number }) => {
  const width = layer.width * scale;
  const height = layer.height * scale;
  const centerX = width / 2;
  const centerY = height / 2;
  const common = {
    fill: layer.fill,
    stroke: layer.stroke,
    strokeWidth: layer.strokeWidth * scale,
    opacity: 1,
    rotation: 0,
    dash: layer.dash,
    lineCap: layer.lineCap,
    lineJoin: layer.lineJoin,
  } as const;

  if (layer.shapeType === "ellipse") {
    return (
      <KonvaEllipse x={centerX} y={centerY} radiusX={width / 2} radiusY={height / 2} {...common} />
    );
  }

  if (layer.shapeType === "line") {
    const localPoints = layer.points ?? [0, 0, layer.width, layer.height];
    return (
      <KonvaLine
        points={localPoints.map((value) => value * scale)}
        closed={layer.closed}
        tension={layer.tension}
        {...common}
      />
    );
  }

  if (layer.shapeType === "polygon") {
    return (
      <KonvaRegularPolygon
        x={centerX}
        y={centerY}
        sides={layer.sides ?? 6}
        radius={layer.radius ? layer.radius * scale : Math.min(width, height) / 2}
        {...common}
      />
    );
  }

  if (layer.shapeType === "star") {
    return (
      <KonvaStar
        x={centerX}
        y={centerY}
        numPoints={layer.sides ?? 5}
        innerRadius={layer.innerRadius ? layer.innerRadius * scale : Math.min(width, height) / 4}
        outerRadius={layer.outerRadius ? layer.outerRadius * scale : Math.min(width, height) / 2}
        {...common}
      />
    );
  }

  if (layer.shapeType === "ring") {
    return (
      <KonvaRing
        x={centerX}
        y={centerY}
        innerRadius={layer.innerRadius ? layer.innerRadius * scale : Math.min(width, height) / 4}
        outerRadius={layer.outerRadius ? layer.outerRadius * scale : Math.min(width, height) / 2}
        {...common}
      />
    );
  }

  if (layer.shapeType === "arc") {
    return (
      <KonvaArc
        x={centerX}
        y={centerY}
        innerRadius={layer.innerRadius ? layer.innerRadius * scale : Math.min(width, height) / 4}
        outerRadius={layer.outerRadius ? layer.outerRadius * scale : Math.min(width, height) / 2}
        angle={layer.angle ?? 120}
        clockwise={layer.clockwise}
        {...common}
      />
    );
  }

  return (
    <KonvaPath
      x={0}
      y={0}
      scaleX={scale}
      scaleY={scale}
      data={layer.pathData ?? "M0 0"}
      {...common}
    />
  );
};

const FrameLayerNode = ({ layer, scale }: { layer: LayoutTemplateFrameLayer; scale: number }) => (
  <>
    {layer.fill ? (
      <KonvaRect
        x={0}
        y={0}
        width={layer.width * scale}
        height={layer.height * scale}
        fill={layer.fill}
        cornerRadius={layer.cornerRadius * scale}
      />
    ) : null}
    <KonvaRect
      x={layer.insetPadding * scale}
      y={layer.insetPadding * scale}
      width={Math.max(0, layer.width * scale - layer.insetPadding * scale * 2)}
      height={Math.max(0, layer.height * scale - layer.insetPadding * scale * 2)}
      stroke={layer.stroke}
      strokeWidth={layer.strokeWidth * scale}
      cornerRadius={layer.cornerRadius * scale}
    />
  </>
);

export const renderLayoutTemplateLayer = (
  layer: SocialPostLayoutTemplateLayerV2,
  scale: number,
): ReactElement | null => {
  if (layer.type === "rect") {
    return (
      <KonvaRect {...toKonvaRectNodeProps(layer as LayoutTemplateRectLayer, scale, "local")} />
    );
  }

  if (layer.type === "text") {
    return (
      <KonvaText {...toKonvaTextNodeProps(layer as LayoutTemplateTextLayer, scale, "local")} />
    );
  }

  if (layer.type === "image" || layer.type === "logo") {
    return <ImageFramePlaceholder layer={layer as LayoutTemplateImageLayer} scale={scale} />;
  }

  if (layer.type === "shape") {
    return <ShapeLayerNode layer={layer as LayoutTemplateShapeLayer} scale={scale} />;
  }

  return <FrameLayerNode layer={layer as LayoutTemplateFrameLayer} scale={scale} />;
};

export const LayoutTemplateCanvasSurface = ({
  template,
  className,
  maxWidth = 620,
  pasteboardPadding = 160,
  onBackgroundSelect,
  renderCanvas,
  renderOverlay,
}: {
  template: SocialPostLayoutTemplateDefinitionV2;
  className?: string;
  maxWidth?: number;
  pasteboardPadding?: number;
  onBackgroundSelect?: () => void;
  renderCanvas: (args: LayoutTemplateCanvasSurfaceRenderArgs) => ReactNode;
  renderOverlay?: (args: LayoutTemplateCanvasSurfaceRenderArgs) => ReactNode;
}) => {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const sortedLayers = getSortedTemplateCanvasLayers(template);
  const canvas = template.canonicalScene.canvas;
  const measuredWidth = containerWidth > 0 ? Math.max(containerWidth, 280) : canvas.width;
  const previewWidth = Math.min(measuredWidth, maxWidth);
  const scale = previewWidth / canvas.width;
  const previewHeight = Math.round(canvas.height * scale);
  const pasteboardFrame = resolveLayoutTemplatePasteboardFrame({
    canvas,
    layers: sortedLayers,
    padding: pasteboardPadding,
    scale,
  });
  const renderArgs = { pasteboardFrame, previewHeight, previewWidth, scale, sortedLayers };

  useEffect(() => {
    if (!container || typeof ResizeObserver === "undefined" || maxWidth <= 280) {
      return;
    }

    const updateWidth = (nextWidth: number) => {
      setContainerWidth(Math.max(0, Math.floor(nextWidth)));
    };

    updateWidth(container.clientWidth);
    const observer = new ResizeObserver((entries) => {
      updateWidth(entries[0]?.contentRect.width ?? container.clientWidth);
    });
    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [container, maxWidth]);

  return (
    <div
      ref={setContainer}
      className={cn("overflow-auto rounded-md border bg-muted/40 p-3", className)}
    >
      <div
        className="relative mx-auto shadow-sm"
        style={{ width: pasteboardFrame.stageWidth, height: pasteboardFrame.stageHeight }}
      >
        <Stage width={pasteboardFrame.stageWidth} height={pasteboardFrame.stageHeight}>
          <Layer>
            <KonvaRect
              x={0}
              y={0}
              width={pasteboardFrame.stageWidth}
              height={pasteboardFrame.stageHeight}
              fill="rgba(15, 23, 42, 0.035)"
              onMouseDown={onBackgroundSelect}
              onTouchStart={onBackgroundSelect}
            />
            <KonvaRect
              x={pasteboardFrame.canvasX}
              y={pasteboardFrame.canvasY}
              width={previewWidth}
              height={previewHeight}
              fill={canvas.backgroundFill}
              shadowColor="rgba(15, 23, 42, 0.18)"
              shadowBlur={16}
              shadowOffsetY={8}
              shadowOpacity={0.45}
              onMouseDown={onBackgroundSelect}
              onTouchStart={onBackgroundSelect}
            />
            <KonvaGroup x={pasteboardFrame.canvasX} y={pasteboardFrame.canvasY}>
              {renderCanvas(renderArgs)}
            </KonvaGroup>
            <KonvaRect
              x={pasteboardFrame.canvasX}
              y={pasteboardFrame.canvasY}
              width={previewWidth}
              height={previewHeight}
              stroke="rgba(15, 23, 42, 0.24)"
              strokeWidth={1}
              listening={false}
            />
          </Layer>
        </Stage>
        {renderOverlay?.(renderArgs)}
      </div>
    </div>
  );
};

const StaticLayoutTemplateLayerNode = ({
  layer,
  scale,
}: {
  layer: SocialPostLayoutTemplateLayerV2;
  scale: number;
}) => (
  <KonvaGroup
    x={layer.x * scale}
    y={layer.y * scale}
    width={layer.width * scale}
    height={layer.height * scale}
    rotation={layer.rotation}
    opacity={layer.opacity}
    visible={layer.visible}
    listening={false}
  >
    {renderLayoutTemplateLayer(layer, scale)}
  </KonvaGroup>
);

export const LayoutTemplateCanvasPreview = ({
  template,
  label,
  className,
  maxWidth = 112,
  pasteboardPadding = 0,
}: {
  template: SocialPostLayoutTemplateDefinitionV2;
  label: string;
  className?: string;
  maxWidth?: number;
  pasteboardPadding?: number;
}) => (
  <div
    role="img"
    aria-label={label}
    className={cn("overflow-hidden rounded-lg border bg-muted/30", className)}
  >
    <LayoutTemplateCanvasSurface
      template={template}
      className="pointer-events-none border-0 bg-transparent p-0"
      maxWidth={maxWidth}
      pasteboardPadding={pasteboardPadding}
      renderCanvas={({ scale, sortedLayers }) =>
        sortedLayers.map((layer) => (
          <StaticLayoutTemplateLayerNode key={layer.id} layer={layer} scale={scale} />
        ))
      }
    />
  </div>
);
